import { Component, computed, DestroyRef, effect, inject, input, OnDestroy, OnInit } from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { TitleComponent } from '@app/shared/components';
import { TranslatePipe } from '@ngx-translate/core';
import { TextComponent, SubmitComponent, RadioComponent } from '@app/shared/forms';
import { CloseComponent } from '../shared/components';
import { StudentsFacade } from '@app/admin/store/students';
import { ActivatedRoute, Router } from '@angular/router';
import { StudentFacade } from '@app/admin/store/student';
import { AvatarComponent } from '../../avatar/avatar.component';
import { UserRole } from '@app/shared/types';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, tap } from 'rxjs';
import { FormValidators } from '@corelabs/angular/forms';

@Component({
    selector: 'app-edit-student',
    templateUrl: './edit-student.component.html',
    styleUrl: './edit-student.component.css',
    imports: [ReactiveFormsModule, TranslatePipe, TextComponent, SubmitComponent, CloseComponent, TitleComponent, AvatarComponent, RadioComponent],
    host: {
        id: 'students',
        role: 'dialog',
        'aria-modal': 'true',
    },
})
export class EditStudentComponent implements OnInit, OnDestroy {
    private readonly router = inject(Router);
    private readonly formBuilder = inject(NonNullableFormBuilder);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly destroyRef = inject(DestroyRef);
    private readonly studentFacade = inject(StudentFacade);
    private readonly studentsFacade = inject(StudentsFacade);

    readonly studentId = input.required<string>();
    readonly student = computed(() => this.students()?.find((s) => s.id === this.studentId()));
    readonly students = this.studentsFacade.students;
    readonly updating = this.studentFacade.updating;

    readonly form = this.formBuilder.group({
        firstName: this.formBuilder.control('', [FormValidators.Required, FormValidators.Text, FormValidators.MaxLength(50)]),
        lastName: this.formBuilder.control('', [FormValidators.Required, FormValidators.Text, FormValidators.MaxLength(50)]),
        email: this.formBuilder.control('', [FormValidators.Required, FormValidators.Text, FormValidators.Email]),
        role: this.formBuilder.control<UserRole | null>(null),
    });

    constructor() {
        effect(() => {
            const student = this.student();
            if (!student) return;

            this.form.patchValue({
                firstName: student.firstName,
                lastName: student.lastName,
                email: student.email,
                role: student.role,
            });
        });
    }

    ngOnInit() {
        this.studentFacade.updated$
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                filter((updated) => updated === true),
                tap(() => this.router.navigate(['..'], { relativeTo: this.activatedRoute })),
            )
            .subscribe();
    }

    ngOnDestroy() {
        this.studentFacade.clear();
    }

    onSubmit() {
        this.form.markAllAsTouched();
        const value = this.form.getRawValue();

        this.studentFacade.updateStudent(this.studentId(), value);
    }
}
